import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import { motion } from "framer-motion";

const Login = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('is_admin')
          .eq('id', session.user.id)
          .single();
        if (profile?.is_admin) {
          navigate('/admin');
        } else {
          navigate('/app');
        }
      }
    };
    checkSession();
  }, [navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;

      const { data: profile } = await supabase
        .from('profiles')
        .select('is_admin')
        .eq('id', data.user.id)
        .single();

      toast({
        title: "Login succesvol",
        description: "Je bent nu ingelogd.",
      });

      if (profile?.is_admin) {
        navigate('/admin');
      } else {
        navigate('/app');
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Onbekende fout bij inloggen';
      toast({
        variant: "destructive",
        title: "Login mislukt",
        description: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />

      <main className="flex-grow flex items-center justify-center px-4 py-16 md:py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-[440px]"
        >
          {/* Header */}
          <div className="text-center mb-10">
            <p
              className="text-black text-[11px] uppercase tracking-[0.2em] font-medium mb-4"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              Mijn Vermado
            </p>
            <h1
              className="text-black text-3xl md:text-4xl font-medium tracking-[0.06em] leading-[1.1]"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              INLOGGEN
            </h1>
          </div>

          {/* Form Card */}
          <div className="bg-[#f2f2f0] rounded-2xl p-8 md:p-10">
            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <label
                  htmlFor="email"
                  className="block text-black text-[11px] uppercase tracking-[0.15em] font-medium mb-2.5"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  E-mailadres
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="naam@voorbeeld.nl"
                  className="w-full bg-white text-black text-sm placeholder:text-black/30 px-4 py-3.5 rounded-xl outline-none focus:ring-2 focus:ring-[#B1A06D]/30 transition-shadow"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-2.5">
                  <label
                    htmlFor="password"
                    className="text-black text-[11px] uppercase tracking-[0.15em] font-medium"
                    style={{ fontFamily: "'Manrope', sans-serif" }}
                  >
                    Wachtwoord
                  </label>
                  <a
                    href="#"
                    className="text-[11px] text-[#B1A06D] hover:text-[#96884e] tracking-[0.05em] transition-colors"
                    style={{ fontFamily: "'Manrope', sans-serif" }}
                  >
                    Vergeten?
                  </a>
                </div>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  className="w-full bg-white text-black text-sm placeholder:text-black/30 px-4 py-3.5 rounded-xl outline-none focus:ring-2 focus:ring-[#B1A06D]/30 transition-shadow"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-black text-white text-xs tracking-[0.1em] uppercase py-4 rounded-full font-medium hover:bg-black/85 transition-colors disabled:opacity-40 disabled:cursor-not-allowed mt-3"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Inloggen...
                  </span>
                ) : 'Inloggen'}
              </button>
            </form>
          </div>

          {/* Bottom Link */}
          <div className="text-center mt-8">
            <p
              className="text-black/50 text-[12px] tracking-[0.03em]"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              Nog geen account?{' '}
              <a
                href="/contact"
                className="text-black font-medium hover:text-[#B1A06D] transition-colors"
              >
                Neem contact op
              </a>
            </p>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default Login;
